import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowDown, PanelLeft, Send, Square } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import {
  CHAT_MESSAGE_TOO_LONG_COPY,
  chatMessageTooLong,
} from "@sill/shared/chat/message-limit";
import sillLogo from "../../assets/sill-logo.svg";
import { useStickToBottom } from "../../hooks/useStickToBottom";
import type { AdvisorMessage } from "../../lib/advisor-types";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { AdvisorHydrationSkeleton } from "../ui/skeleton";
import { AdvisorMessageBubble } from "./message-bubble";

interface AdvisorChatSurfaceProps {
  messages: readonly AdvisorMessage[];
  onSubmitPrompt: (text: string) => void;
  onStopStreaming?: () => void;
  onFollowup: (text: string) => void;
  suggestions: readonly string[];
  contextRail?: ReactNode;
  currentProfileRecencyToken?: string | null;
  initialDraft?: string;
  isStreaming?: boolean;
  /** When true the workspace pane is hidden and widgets render inline. */
  isWorkspaceCollapsed?: boolean;
  onToggleWorkspace?: () => void;
  isHydrating?: boolean;
  className?: string;
  /**
   * Below `xl:`, this surface reserves extra bottom space (the outer
   * section's padding + the fixed composer's bottom offset) so content and
   * the composer clear the full-page `MobileTabBar` (`DashboardPage.tsx`).
   * The `AdvisorDrawer` (UI review, iteration 2, finding 5) renders as a
   * full-height bottom sheet below `md:` with NO tab bar underneath it —
   * that reservation there is dead space (~104px band below the
   * composer). Defaults to `true` so the full page keeps its byte-for-byte
   * layout; the drawer passes `false`.
   */
  reserveMobileTabBarSpace?: boolean;
  /**
   * `"drawer"` (2026-10-08): render the PHONE layout at every viewport —
   * the Ask Sill drawer is a ~420px column, so the page's `xl:` (viewport)
   * breakpoints would otherwise hand it the wide desktop layout, and below
   * `xl:` its `fixed` composer escaped the drawer and spanned the window.
   * Same components, sized for the column: in-flow composer pinned to the
   * column's bottom, tighter padding/type, auto-growing input, no workspace
   * pane. Defaults to `"page"` so the full page is byte-for-byte unchanged.
   */
  variant?: "page" | "drawer";
}

type SplitLayoutStyle = CSSProperties & {
  "--advisor-chat-min-width"?: string;
  "--advisor-workspace-active-width"?: string;
  "--advisor-workspace-splitter-width"?: string;
  "--advisor-workspace-min-width"?: string;
};

export function AdvisorChatSurface({
  className,
  contextRail,
  currentProfileRecencyToken,
  initialDraft,
  isHydrating = false,
  isStreaming = false,
  isWorkspaceCollapsed = false,
  messages,
  onFollowup,
  onSubmitPrompt,
  onStopStreaming,
  onToggleWorkspace,
  reserveMobileTabBarSpace = true,
  suggestions,
  variant = "page",
}: AdvisorChatSurfaceProps) {
  const isDrawer = variant === "drawer";
  const [draft, setDraft] = useState(initialDraft ?? "");
  const [tooLong, setTooLong] = useState(false);
  const hasWorkspaceRail = Boolean(contextRail);
  const hasWorkspacePane = hasWorkspaceRail && !isWorkspaceCollapsed;
  const isWorkspaceRailCollapsed = hasWorkspaceRail && !hasWorkspacePane;
  const layout = hasWorkspacePane
    ? "split"
    : isWorkspaceRailCollapsed
      ? "workspace-collapsed"
      : "single";
  const [workspaceWidth, setWorkspaceWidth] = useState<number | null>(null);
  const splitLayoutRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const isResizingRef = useRef(false);
  const prefersReducedMotion = useReducedMotion() === true;
  // Stick-to-bottom: the view follows new/streaming content only while the user
  // is parked at the bottom; once they scroll up, a "jump to latest" control
  // appears instead of yanking them down. `scrollRef` is the message column
  // (where wheel/touch gestures originate), `bottomRef` is the bottom sentinel.
  // The shared hook owns the IntersectionObserver + gesture detection.
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const hasMessages = messages.length > 0;
  const chatMinWidth = 520;
  const workspaceMinWidth = 300;
  const workspaceDefaultWidth = 300;
  const workspaceRailWidth = 44;
  const workspaceSplitterWidth = hasWorkspacePane ? 24 : 0;
  const activeWorkspaceWidth = workspaceWidth ?? workspaceDefaultWidth;
  // The chat column always stays centered within whatever horizontal space is
  // left to it — independent of the history sidebar or workspace pane. Opening
  // the workspace narrows the chat pane; the content re-centers inside the
  // narrower pane rather than snapping to the left edge.
  const splitLayoutStyle: SplitLayoutStyle | undefined = hasWorkspaceRail
    ? {
        "--advisor-chat-min-width": `${chatMinWidth}px`,
        "--advisor-workspace-active-width": hasWorkspacePane
          ? `${activeWorkspaceWidth}px`
          : `${workspaceRailWidth}px`,
        "--advisor-workspace-splitter-width": `${workspaceSplitterWidth}px`,
        "--advisor-workspace-min-width": `${workspaceMinWidth}px`,
      }
    : undefined;

  // Show a thinking placeholder whenever a turn is in flight but we have
  // nothing from the advisor yet — specifically, the last message is the
  // user's and we're still streaming. The moment the first text_delta or
  // render lands, the store mutates the last message into an advisor one
  // and this condition flips false naturally.
  const lastMessage = messages[messages.length - 1];
  const showThinking =
    isStreaming && (!lastMessage || lastMessage.role === "user");
  const latestAdvisorText =
    lastMessage?.role === "advisor" ? (lastMessage.text ?? "") : "";
  const latestRenderCount =
    lastMessage?.role === "advisor"
      ? (lastMessage.renders?.length ?? (lastMessage.render ? 1 : 0))
      : 0;

  const { isPinnedToBottom, scrollToBottom, pinToBottom } = useStickToBottom({
    scrollRef,
    sentinelRef: bottomRef,
    enabled: hasMessages,
  });

  // Stick-to-bottom: keep the latest content in view ONLY while pinned. Text
  // deltas mutate the latest advisor message without changing the message
  // count, so this keys off content/render changes too. Instant ("auto") while
  // streaming (frequent), smooth for discrete additions.
  useEffect(() => {
    if (!hasMessages || !isPinnedToBottom) return;
    scrollToBottom(isStreaming ? "auto" : "smooth");
  }, [
    hasMessages,
    isPinnedToBottom,
    isStreaming,
    latestAdvisorText,
    latestRenderCount,
    messages.length,
    scrollToBottom,
    showThinking,
  ]);

  useEffect(() => {
    if (hasMessages) return;
    composerRef.current?.focus();
  }, [hasMessages]);

  const submit = () => {
    if (isStreaming) return;
    const trimmed = draft.trim();
    if (!trimmed) return;
    // Over the server cap: keep the draft so nothing the user typed is lost.
    if (chatMessageTooLong(trimmed)) {
      setTooLong(true);
      return;
    }
    setTooLong(false);
    // Sending always returns the user to the bottom to watch the reply land.
    pinToBottom();
    onSubmitPrompt(trimmed);
    setDraft("");
  };

  // Follow-up chips inside a bubble also re-pin so the new turn is followed.
  const handleFollowup = (text: string) => {
    pinToBottom();
    onFollowup(text);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit();
  };

  // Enter submits, Shift+Enter inserts a newline. Matches chat-app
  // conventions and is what everyone's muscle memory expects.
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  useEffect(() => {
    if (!hasWorkspacePane) return;

    const stopResize = () => {
      isResizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (!isResizingRef.current) return;
      const rect = splitLayoutRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0) return;

      const nextWidth = rect.right - event.clientX;
      const workspaceMaxWidth = Math.max(
        420,
        Math.floor((rect.width - 24) * 0.5),
      );
      setWorkspaceWidth(
        Math.min(workspaceMaxWidth, Math.max(workspaceMinWidth, nextWidth)),
      );
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopResize);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopResize);
      stopResize();
    };
  }, [hasWorkspacePane, workspaceMinWidth]);

  const startResize = () => {
    isResizingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const composer = (
    <>
    <form
      className={cn(
        "flex bg-background shadow-sm focus-within:border-primary/40 focus-within:shadow-md",
        isDrawer
          ? cn(
              "w-full items-end gap-2 rounded-[20px] border border-border/70 py-1.5 pl-3.5 pr-1.5",
              !hasMessages && "shadow-[0_12px_40px_rgba(15,32,29,0.08)]",
            )
          : "xl:bg-background/95 xl:backdrop-blur",
        isDrawer
          ? null
          : hasMessages
          ? cn(
              "fixed inset-x-4 z-30 mx-auto min-h-[84px] w-auto max-w-full items-center gap-2 rounded-[24px] border border-border/60 py-2 pl-4 pr-2 xl:static xl:inset-auto xl:bottom-auto xl:z-auto xl:min-h-[102px] xl:w-full xl:max-w-[800px] xl:rounded-[26px] xl:py-3 xl:pl-6 xl:pr-3",
              reserveMobileTabBarSpace
                ? "bottom-[calc(6.5rem+env(safe-area-inset-bottom))]"
                : "bottom-[calc(1rem+env(safe-area-inset-bottom))]",
            )
          : "min-h-[84px] w-full max-w-full items-center gap-2 rounded-[24px] border border-border/70 py-2 pl-4 pr-2 shadow-[0_18px_60px_rgba(15,32,29,0.08)] xl:min-h-[102px] xl:max-w-[800px] xl:rounded-[26px] xl:py-3 xl:pl-6 xl:pr-3",
        !isDrawer && !hasMessages && "mx-auto",
      )}
      data-state={hasMessages ? "active" : "empty"}
      onSubmit={handleSubmit}
    >
      <textarea
        className={
          isDrawer
            ? // Starts one line tall and grows with the text (field-sizing),
              // capped so a long draft scrolls inside the box instead of
              // pushing the conversation out of the column.
              "max-h-40 min-h-9 flex-1 resize-none overflow-y-auto bg-transparent py-1.5 text-[15px] leading-6 text-foreground [field-sizing:content] placeholder:text-muted-foreground focus:outline-none"
            : "min-h-[3rem] flex-1 resize-none overflow-hidden bg-transparent py-2 text-base leading-6 text-foreground placeholder:text-muted-foreground focus:outline-none xl:min-h-[4rem] xl:py-3 xl:text-[1.05rem] xl:leading-7"
        }
        data-slot="advisor-composer"
        ref={composerRef}
        onChange={(event) => {
          setDraft(event.target.value);
          if (tooLong) setTooLong(false);
        }}
        onKeyDown={handleKeyDown}
        placeholder={
          hasMessages
            ? "Ask about payments, homes, or next steps..."
            : "Ask about payments, homes, or next steps"
        }
        rows={isDrawer ? 1 : 2}
        value={draft}
      />
      <div
        className={cn(
          "flex items-center",
          isDrawer || hasMessages ? "contents" : "justify-end",
        )}
      >
        {isStreaming ? (
          <Button
            aria-label="Stop response"
            className={cn(
              "rounded-full !p-0",
              isDrawer
                ? // `!min-h-9`: the DS Button's base `min-h-11` otherwise wins
                  // (the app's `cn` joins, it doesn't merge) — a 44px button
                  // made the box taller than the 36px input and threw the
                  // placeholder ~3px off-centre.
                  "h-9 !min-h-9 w-9"
                : cn("h-10 w-10", hasMessages ? "xl:h-11 xl:w-11" : "xl:h-12 xl:w-12"),
            )}
            data-slot="advisor-composer-stop"
            disabled={!onStopStreaming}
            onClick={onStopStreaming}
            title="Stop response"
            type="button"
          >
            <Square aria-hidden className="h-4 w-4 shrink-0 fill-current" />
            <span className="sr-only">Stop response</span>
          </Button>
        ) : (
          <Button
            className={cn(
              "rounded-full !p-0",
              isDrawer
                ? // `!min-h-9`: the DS Button's base `min-h-11` otherwise wins
                  // (the app's `cn` joins, it doesn't merge) — a 44px button
                  // made the box taller than the 36px input and threw the
                  // placeholder ~3px off-centre.
                  "h-9 !min-h-9 w-9"
                : cn("h-10 w-10", hasMessages ? "xl:h-11 xl:w-11" : "xl:h-12 xl:w-12"),
            )}
            data-slot="advisor-composer-send"
            disabled={draft.trim().length === 0}
            type="submit"
          >
            <Send aria-hidden className={cn("shrink-0", isDrawer ? "h-4 w-4" : "h-5 w-5")} />
            <span className="sr-only">Send</span>
          </Button>
        )}
      </div>
    </form>
    {tooLong ? (
      <p role="alert" className="mt-2 px-2 text-sm text-destructive">
        {CHAT_MESSAGE_TOO_LONG_COPY}
      </p>
    ) : null}
    </>
  );

  return (
    <section
      className={cn(
        isDrawer
          ? "h-full min-h-0 w-full min-w-0"
          : cn(
              "w-full min-w-0 px-4 sm:px-5 xl:h-full xl:min-h-0 xl:px-0 xl:pb-0",
              reserveMobileTabBarSpace
                ? "pb-28"
                : "pb-[calc(1rem+env(safe-area-inset-bottom))]",
            ),
        className,
      )}
      data-layout={layout}
      data-variant={variant}
    >
      <div
        ref={splitLayoutRef}
        className={cn(
          isDrawer
            ? "grid h-full min-h-0 w-full min-w-0 grid-cols-1"
            : "grid w-full min-w-0 grid-cols-1 xl:h-full xl:min-h-0 xl:items-stretch",
          hasWorkspaceRail &&
            "motion-safe:transition-[grid-template-columns] motion-safe:duration-[420ms] motion-safe:ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none xl:[grid-template-columns:minmax(var(--advisor-chat-min-width),1fr)_var(--advisor-workspace-splitter-width)_var(--advisor-workspace-active-width)]",
        )}
        data-slot="advisor-split-layout"
        style={splitLayoutStyle}
      >
        <div
          className={cn(
            isDrawer
              ? "relative flex h-full min-h-0 min-w-0 flex-col"
              : cn(
                  "relative flex min-h-[calc(100vh-14rem)] min-w-0 flex-col xl:h-full xl:min-h-0 xl:min-w-0",
                  hasWorkspacePane ? "xl:pl-8 xl:pr-4" : "xl:px-8",
                ),
          )}
          data-slot="advisor-chat-pane"
        >
          <div
            className={cn(
              // The scroll lives on this column. Widen the box by the gutter
              // (800 → 848) and pad it (px-6) so the ~800px text measure is
              // preserved while the scrollbar renders in the right-hand margin
              // instead of hugging the text — padding-right on an overflow
              // container sits between the content and the scrollbar track.
              isDrawer
                ? "min-h-0 min-w-0 flex-1 overflow-y-auto px-4"
                : "min-w-0 flex-1 xl:mx-auto xl:w-full xl:max-w-[848px] xl:overflow-y-auto xl:px-6",
              // space-y-8 (inter-message gap) is gated on hasMessages: the empty
              // state uses min-h-full, so ANY following sibling + space-y-8 would
              // add a 32px margin and overflow the column (phantom scroll). Only
              // the message list needs the gap.
              hasMessages && (isDrawer ? "space-y-6 pb-3" : "space-y-8 xl:pb-2"),
            )}
            data-slot="advisor-chat-content-column"
            ref={scrollRef}
          >
            {isHydrating ? (
              <AdvisorHydrationSkeleton />
            ) : messages.length === 0 ? (
              <EmptyState
                hideSuggestions={hasWorkspaceRail}
                onSuggestion={(text) => setDraft(text)}
                suggestions={suggestions}
                composer={composer}
                centerContent
                compact={isDrawer}
              />
            ) : (
              <ul
                className={
                  isDrawer
                    ? "w-full space-y-5 pt-4"
                    : "mx-auto w-full max-w-full space-y-6 pt-16 xl:max-w-[800px]"
                }
                data-slot="advisor-messages"
              >
                {messages.map((message) => (
                  <li key={message.id}>
                    <AdvisorMessageBubble
                      currentProfileRecencyToken={currentProfileRecencyToken}
                      inlineRenderClassName={
                        hasWorkspacePane ? "xl:hidden" : undefined
                      }
                      message={message}
                      onFollowup={handleFollowup}
                      showInlineRender={Boolean(
                        message.render || message.renders?.length,
                      )}
                      streaming={
                        isStreaming &&
                        message.id === lastMessage?.id &&
                        message.role === "advisor"
                      }
                    />
                  </li>
                ))}
                {showThinking ? (
                  <li data-slot="advisor-thinking">
                    <ThinkingBubble />
                  </li>
                ) : null}
              </ul>
            )}
            {!isDrawer && (hasMessages || isHydrating) ? (
              <div
                aria-hidden
                className="h-[calc(12rem+env(safe-area-inset-bottom))] xl:hidden"
                data-slot="advisor-mobile-floating-spacer"
              />
            ) : null}
            {/* Bottom sentinel for stick-to-bottom. Only rendered with messages
                — useStickToBottom is `enabled: hasMessages`, so when the chat is
                empty this sentinel is unused, and rendering it would add a
                `space-y-8` (32px) gap below the min-h-full empty state, making
                the empty chat scroll by 32px with nothing in it. */}
            {hasMessages ? <div ref={bottomRef} aria-hidden /> : null}
          </div>

          {hasMessages && !isHydrating ? (
            <div
              className={cn(
                "relative",
                // Drawer: the composer is in flow at the column's bottom.
                isDrawer &&
                  "shrink-0 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-2",
              )}
            >
              {/* Jump-to-latest: only while the user has scrolled up off the
                  bottom (desktop scroll column). Tap to smooth-scroll down and
                  re-pin. */}
              <AnimatePresence>
                {!isPinnedToBottom ? (
                  <motion.div
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    className={cn(
                      "pointer-events-none absolute inset-x-0 z-10 justify-center",
                      isDrawer ? "-top-12 flex" : "-top-14 hidden xl:flex",
                    )}
                    exit={
                      prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }
                    }
                    initial={
                      prefersReducedMotion
                        ? false
                        : { opacity: 0, scale: 0.9, y: 8 }
                    }
                    key="advisor-jump-to-latest"
                    transition={{ type: "spring", duration: 0.3, bounce: 0 }}
                  >
                    <button
                      aria-label="Scroll to latest messages"
                      className="pointer-events-auto inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_8px_24px_rgba(15,32,29,0.18)] ring-1 ring-primary/20 transition-[background-color,transform] duration-150 hover:bg-primary/90 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                      data-slot="advisor-jump-to-latest"
                      onClick={() => {
                        pinToBottom();
                        scrollToBottom("smooth");
                      }}
                      type="button"
                    >
                      <ArrowDown aria-hidden className="h-5 w-5" />
                    </button>
                  </motion.div>
                ) : null}
              </AnimatePresence>
              {composer}
            </div>
          ) : null}
        </div>

        {hasWorkspaceRail ? (
          <div
            aria-hidden={hasWorkspacePane ? undefined : "true"}
            className={cn(
              "hidden min-w-0 overflow-hidden xl:flex xl:h-full xl:min-h-0 xl:w-full xl:shrink-0 xl:self-stretch xl:justify-center motion-safe:transition-[opacity] motion-safe:duration-[420ms] motion-safe:ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none",
              hasWorkspacePane
                ? "opacity-100"
                : "pointer-events-none opacity-0",
            )}
            data-slot="advisor-splitter-track"
          >
            {hasWorkspacePane ? (
              <button
                aria-label="Resize workspace"
                className="group relative flex h-full w-10 cursor-col-resize items-center justify-center focus:outline-none"
                data-slot="advisor-splitter"
                onPointerDown={startResize}
                type="button"
              >
                <span
                  aria-hidden
                  className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border transition-colors duration-150 group-hover:bg-border-soft group-focus-visible:bg-primary/40"
                />
                <span
                  aria-hidden
                  className="relative h-10 w-1 rounded-full bg-border opacity-0 transition-all duration-150 ease-out group-hover:bg-foreground/30 group-hover:opacity-100 group-focus-visible:bg-primary/60 group-focus-visible:opacity-100"
                />
              </button>
            ) : null}
          </div>
        ) : null}

        {hasWorkspaceRail ? (
          <div
            className="relative hidden xl:block xl:h-full xl:min-w-0 xl:overflow-hidden"
            data-slot="advisor-workspace-stage"
          >
            <aside
              aria-hidden={hasWorkspacePane ? undefined : "true"}
              className={cn(
                "absolute inset-0 flex h-full min-w-0 flex-col self-start overflow-hidden motion-safe:transition-[opacity,transform] motion-safe:duration-[420ms] motion-safe:ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none",
                hasWorkspacePane
                  ? "translate-x-0 opacity-100"
                  : "pointer-events-none translate-x-3 opacity-0",
              )}
              data-slot="advisor-model-pane"
            >
              <div className="h-full min-h-0">{contextRail}</div>
            </aside>

            <aside
              aria-hidden={isWorkspaceRailCollapsed ? undefined : "true"}
              aria-label="Collapsed workspace"
              className={cn(
                "absolute inset-0 flex h-full min-w-0 flex-col items-center border-l border-border/60 bg-bg-warm/70 motion-safe:transition-[opacity,transform] motion-safe:duration-[420ms] motion-safe:ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none",
                isWorkspaceRailCollapsed
                  ? "translate-x-0 opacity-100"
                  : "pointer-events-none translate-x-2 opacity-0",
              )}
              data-slot="advisor-workspace-collapsed-rail"
            >
              <button
                aria-expanded="false"
                aria-label="Open workspace"
                className="mt-3 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-[background-color,color,transform,opacity] hover:bg-muted/60 hover:text-foreground active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                data-slot="advisor-workspace-restore"
                onClick={onToggleWorkspace}
                title="Open workspace"
                type="button"
              >
                <PanelLeft aria-hidden className="h-4 w-4 scale-x-[-1]" />
              </button>
            </aside>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function ThinkingBubble() {
  return (
    <div
      className="flex w-full items-center gap-1 text-muted-foreground"
      data-role="advisor-thinking"
    >
      <span className="sr-only">Ask Sill is thinking…</span>
      <Dot delay="0ms" />
      <Dot delay="150ms" />
      <Dot delay="300ms" />
    </div>
  );
}

function Dot({ delay }: { delay: string }) {
  return (
    <span
      className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-current"
      style={{ animationDelay: delay }}
    />
  );
}

// EmptyState shows the welcome blurb when no messages exist yet.
//
function EmptyState({
  centerContent = false,
  compact = false,
  composer,
  hideSuggestions,
  onSuggestion,
  suggestions,
}: {
  centerContent?: boolean;
  /** Drawer column: smaller wordmark + padding, one-column suggestions. */
  compact?: boolean;
  composer: ReactNode;
  hideSuggestions?: boolean;
  onSuggestion: (text: string) => void;
  suggestions: readonly string[];
}) {
  return (
    <div
      className={cn(
        "flex min-h-full flex-col justify-center",
        compact ? "py-8" : "px-4 py-16",
        centerContent ? "items-center" : "items-start",
      )}
      data-slot="advisor-empty-state"
    >
      <div
        className={cn(
          "w-full max-w-full space-y-2 text-center",
          compact ? "mb-5" : "mb-4 xl:max-w-[800px]",
          centerContent && "mx-auto",
        )}
        data-slot="advisor-empty-intro"
      >
        <div className="flex items-center justify-center gap-2">
          <span
            className={cn(
              "font-semibold tracking-[-0.03em] text-foreground",
              compact ? "text-2xl" : "text-[2rem]",
            )}
          >
            Ask
          </span>
          <img
            alt=""
            aria-hidden
            className={cn(
              "translate-y-0.5 object-contain",
              compact ? "h-8 w-8" : "h-10 w-10",
            )}
            src={sillLogo}
          />
        </div>
        <p
          className={cn(
            "leading-6 text-muted-foreground",
            compact ? "text-sm" : "text-[15px]",
          )}
        >
          Ask about affordability, rate, and next-step questions.
        </p>
      </div>

      {composer}

      {hideSuggestions ? null : (
        <div
          className={cn(
            "mt-5 grid w-full max-w-full auto-rows-fr gap-2.5",
            compact ? "grid-cols-1" : "sm:grid-cols-2 xl:max-w-[800px]",
            centerContent && "mx-auto",
          )}
          data-slot="advisor-empty-suggestions"
        >
          {suggestions.map((suggestion) => (
            <button
              className="flex h-full min-h-[88px] items-center rounded-2xl border border-transparent bg-card px-5 py-4 text-left text-[15px] leading-6 text-foreground transition-[background-color,border-color,transform] hover:border-primary/40 hover:bg-primary-soft/40 active:scale-[0.96] focus-visible:border-primary/50 focus-visible:bg-primary-soft/50 focus-visible:outline-none"
              key={suggestion}
              onClick={() => onSuggestion(suggestion)}
              type="button"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
